// Tile rebuilds in slices (phase C, gate C5): when something that blocked the navmesh goes (a crate broken, a door
// opened), the tiles it overlapped are rebuilt by staged tile jobs (tilejob.ts), a few units of work per call to
// `step()`, which the game makes once per sim step. The tile in use stays in the mesh, so the crowd keeps walking on
// the old tile while the new one is built, and the last unit swaps it in. Slicing is by units, not by measured time:
// the swap lands on the same step in every run and after a restore (a deterministic sim), and every unit's work is
// bounded by a tile's voxels or a batch of triangles, so a slice's time stays small; the slicer measures each slice
// (wall-clock ms: a count of work under SwiftShader or Node, not a phone's timing) so a game can gate on it.
import type { NavTileJob, NavWorld, TileXY } from "./navmesh";
import type { TileStage } from "./tilejob";

/** How much work a slice does. */
export interface SlicerOptions {
  /** Units of tile work per `step()` (default 1). */
  unitsPerSlice?: number;
  /** Slice times kept for `stats().recent` (default 512). */
  keep?: number;
}

/** The slicer's state as plain data (saves): the tiles waiting and the job in progress. */
export interface SlicerState {
  queue: [number, number][];
  /** The job in progress: its tile, the dynamic shapes it was started with, the units it has done. */
  job: { tile: [number, number]; ids: string[]; done: number } | null;
}

/** One slice's report. */
export interface SliceReport {
  units: number;
  /** Wall-clock milliseconds the slice took. */
  ms: number;
  /** Tiles swapped in (or emptied) by this slice. */
  finished: TileXY[];
  /** The stages this slice ran. */
  stages: TileStage[];
}

/** What the slicer has done since its last reset. */
export interface SlicerStats {
  /** Slices that did work. */
  slices: number;
  units: number;
  tiles: number;
  maxSliceMs: number;
  meanSliceMs: number;
  /** The stage that ran in the longest slice. */
  maxStage: TileStage | null;
  /** The last `keep` working slices' times (ms), oldest first. */
  recent: number[];
}

/** Planted defects, beside what they break (tests only). */
export interface SlicerFaults {
  /** Every queued rebuild runs to the end in one slice: the per-slice time gate must go red. */
  oneSlice: boolean;
}

/** Rebuilds navmesh tiles a few units of work at a time. */
export class NavTileSlicer {
  /** Planted defects (tests only). */
  readonly faults: SlicerFaults = { oneSlice: false };
  private readonly per: number;
  private readonly keep: number;
  private queue: TileXY[] = [];
  private job: NavTileJob | null = null;
  private st = { slices: 0, units: 0, tiles: 0, maxMs: 0, sumMs: 0, maxStage: null as TileStage | null, recent: [] as number[] };

  constructor(
    private readonly world: NavWorld,
    o: SlicerOptions = {},
  ) {
    this.per = Math.max(1, Math.floor(o.unitsPerSlice ?? 1));
    this.keep = o.keep ?? 512;
  }

  /** Queue tiles for a rebuild (each once; a tile whose job is running is queued again, to take the newer shapes). */
  request(tiles: readonly TileXY[]): void {
    for (const tl of tiles) if (!this.queue.some((q) => q.x === tl.x && q.y === tl.y)) this.queue.push({ x: tl.x, y: tl.y });
  }

  /** Take a tile out of the queue, and cancel its job if it is the one in progress (a streamed chunk unloading before
   *  its tile was built). Returns whether it was there. */
  drop(tile: TileXY): boolean {
    const n = this.queue.length;
    this.queue = this.queue.filter((q) => q.x !== tile.x || q.y !== tile.y);
    let hit = this.queue.length !== n;
    if (this.job && this.job.tile.x === tile.x && this.job.tile.y === tile.y) {
      this.job.cancel();
      this.job = null;
      hit = true;
    }
    return hit;
  }

  /** Whether any rebuild is waiting or in progress. */
  busy(): boolean {
    return this.job !== null || this.queue.length > 0;
  }

  /** The tiles waiting (the job in progress first). */
  pending(): TileXY[] {
    return [...(this.job ? [{ ...this.job.tile }] : []), ...this.queue.map((q) => ({ ...q }))];
  }

  /** Run one slice: up to `unitsPerSlice` units of tile work (all of it, with the planted defect on). */
  step(): SliceReport {
    if (!this.busy()) return { units: 0, ms: 0, finished: [], stages: [] };
    const t0 = performance.now();
    const finished: TileXY[] = [];
    const stages: TileStage[] = [];
    let units = 0;
    const all = this.faults.oneSlice;
    while ((all || units < this.per) && this.busy()) {
      if (!this.job) {
        const tile = this.queue.shift()!;
        this.job = this.world.tileJob(tile);
      }
      const stage = this.job.stage;
      if (stage !== "finished") stages.push(stage);
      units++;
      if (this.job.unit()) {
        finished.push({ ...this.job.tile });
        this.job = null;
      }
    }
    const ms = performance.now() - t0;
    const s = this.st;
    s.slices++;
    s.units += units;
    s.tiles += finished.length;
    s.sumMs += ms;
    if (ms > s.maxMs) {
      s.maxMs = ms;
      s.maxStage = stages[0] ?? null;
    }
    s.recent.push(ms);
    if (s.recent.length > this.keep) s.recent.shift();
    return { units, ms, finished, stages };
  }

  /** Run every waiting rebuild to the end now (load time, tests). */
  flush(): TileXY[] {
    const out: TileXY[] = [];
    while (this.busy()) {
      if (!this.job) this.job = this.world.tileJob(this.queue.shift()!);
      this.job.finish();
      out.push({ ...this.job.tile });
      this.job = null;
    }
    return out;
  }

  /** The work since the last reset. */
  stats(reset = false): SlicerStats {
    const s = this.st;
    const out: SlicerStats = { slices: s.slices, units: s.units, tiles: s.tiles, maxSliceMs: s.maxMs, meanSliceMs: s.slices ? s.sumMs / s.slices : 0, maxStage: s.maxStage, recent: [...s.recent] };
    if (reset) this.st = { slices: 0, units: 0, tiles: 0, maxMs: 0, sumMs: 0, maxStage: null, recent: [] };
    return out;
  }

  /** The queue and the job in progress, as plain data. */
  snapshot(): SlicerState {
    return {
      queue: this.queue.map((q) => [q.x, q.y]),
      job: this.job ? { tile: [this.job.tile.x, this.job.tile.y], ids: [...this.job.ids], done: this.job.done } : null,
    };
  }

  /** Put back a snapshot's queue and job: the job is started again with the shapes it had and run to where it was
   *  (its units are deterministic, so the rest of it, and its swap, land as they would have). Restore the world's
   *  dynamic state (`NavWorld.restoreDynamic`) first. */
  restore(s: SlicerState): void {
    this.job?.cancel();
    this.job = null;
    this.queue = s.queue.map(([x, y]) => ({ x, y }));
    if (s.job) {
      const tile = { x: s.job.tile[0], y: s.job.tile[1] };
      this.job = this.world.tileJob(tile, s.job.ids);
      for (let i = 0; i < s.job.done; i++) this.job.unit();
    }
  }
}
